import { motion } from 'framer-motion'
import './Button.css'

/**
 * Button - A versatile button component with variants and animations
 * 
 * @param {ReactNode} children - Button content
 * @param {string} variant - 'primary', 'secondary', 'ghost', or 'outline'
 * @param {string} size - 'sm', 'md', or 'lg'
 * @param {boolean} fullWidth - Whether button takes full width
 * @param {boolean} disabled - Whether button is disabled
 * @param {boolean} loading - Whether button shows loading state
 * @param {string} leftIcon - Icon to show on the left
 * @param {string} rightIcon - Icon to show on the right
 * @param {string} className - Additional CSS classes
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  className = '',
  ...props
}) {
  const buttonVariants = {
    idle: { scale: 1 },
    hover: { 
      scale: 1.02,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
    tap: { 
      scale: 0.98,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
  }

  return (
    <motion.button
      className={`btn btn--${variant} btn--${size} ${fullWidth ? 'btn--full' : ''} ${className}`}
      disabled={disabled || loading}
      variants={buttonVariants}
      initial="idle"
      whileHover={!disabled && !loading ? "hover" : undefined}
      whileTap={!disabled && !loading ? "tap" : undefined}
      {...props}
    >
      {loading ? (
        <span className="btn__spinner" />
      ) : (
        <>
          {leftIcon && <span className="btn__icon btn__icon--left">{leftIcon}</span>}
          <span className="btn__text">{children}</span>
          {rightIcon && <span className="btn__icon btn__icon--right">{rightIcon}</span>}
        </>
      )}
    </motion.button>
  )
}

export default Button

